import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { todayAthens } from "../../../lib/format";
import { useApi } from "../../customer/hooks";
import { mediaUrl } from "../../customer/promoTrack";
import { DateField } from "../../../components/common/DateField";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Switch } from "../../../components/ui/switch";

const inp = "mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-navy outline-none focus:border-brand";
const EMPTY = () => ({ title_el: "", title_en: "", text_el: "", text_en: "", item_codes: [], target: { type: "all", areas: [], customers: [] }, start_date: todayAthens(), end_date: todayAthens(30), active: true });
const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const Field = ({ label, value, onChange, testId, area }) => (
  <label className="block text-xs text-slate-500">{label}
    {area ? <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={`${inp} h-auto py-2`} />
      : <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={inp} />}
  </label>
);

const Checks = ({ options, selected, onToggle, testPrefix }) => (
  <div className="max-h-44 min-w-0 space-y-0.5 overflow-y-auto overflow-x-hidden rounded-xl border border-slate-200 p-1">
    {options.map((o) => (
      <label key={o.value} className="flex min-h-[40px] cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-navy hover:bg-slate-50">
        <input type="checkbox" checked={selected.includes(o.value)} onChange={() => onToggle(o.value)} data-testid={`${testPrefix}-${o.value}`} className="h-4 w-4 accent-[#002068]" />
        <span className="min-w-0 truncate">{o.label}</span>
      </label>
    ))}
  </div>
);

const ItemPicker = ({ selected, onToggle }) => {
  const { t, lang } = useI18n();
  const items = useApi("adminItems", "/catalogue/items");
  const [q, setQ] = useState("");
  const options = useMemo(() => (items.data || [])
    .filter((i) => !q || `${i.code} ${i.name_el} ${i.name_en} ${i.brand}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => Number(selected.includes(b.code)) - Number(selected.includes(a.code)))
    .map((i) => ({ value: i.code, label: `${i.code} · ${i[`name_${lang}`]}` })), [items.data, q, lang, selected]);
  return (
    <div>
      <p className="text-xs text-slate-500">{t("promo.items")} ({selected.length})</p>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("promo.searchItems")} data-testid="promo-item-search" className={`${inp} mb-1`} />
      <Checks options={options} selected={selected} onToggle={onToggle} testPrefix="promo-item" />
    </div>
  );
};

const TargetPicker = ({ target, set }) => {
  const { t, lang } = useI18n();
  const areas = useApi("deliveryAreas", "/delivery/areas");
  const customers = useApi("adminCustomers", "/customers");
  const types = [["all", "promo.targetAll"], ["areas", "promo.targetAreas"], ["customers", "promo.targetCustomers"]];
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">{t("promo.target")}</p>
      <div className="flex flex-wrap gap-1.5">
        {types.map(([v, k]) => (
          <button key={v} type="button" onClick={() => set({ ...target, type: v })} data-testid={`promo-target-${v}`}
            className={`h-10 rounded-full px-4 text-sm font-medium ${target.type === v ? "bg-navy text-white" : "bg-slate-100 text-navy"}`}>{t(k)}</button>
        ))}
      </div>
      {target.type === "areas" && <Checks testPrefix="promo-area" selected={target.areas} onToggle={(v) => set({ ...target, areas: toggle(target.areas, v) })}
        options={(areas.data || []).map((a) => ({ value: a.code, label: a[`name_${lang}`] }))} />}
      {target.type === "customers" && <Checks testPrefix="promo-customer" selected={target.customers} onToggle={(v) => set({ ...target, customers: toggle(target.customers, v) })}
        options={(customers.data || []).map((c) => ({ value: c.code, label: `${c.code} · ${c.name}` }))} />}
    </div>
  );
};

const OfferLines = ({ codes, lines, set }) => {
  const { t } = useI18n();
  if (!codes.length) return null;
  const upd = (c, k, v) => set({ ...lines, [c]: { uom: "box", min_qty: 1, ...lines[c], [k]: v } });
  return (
    <div className="space-y-1.5" data-testid="offer-lines">
      <p className="text-xs text-slate-500">{t("x.offers.offerPrice")} / {t("x.offers.minQty")}</p>
      {codes.map((c) => (
        <div key={c} className="grid grid-cols-[1fr_72px_96px_64px] items-center gap-1.5 text-sm">
          <span className="truncate font-mono text-xs text-navy">{c}</span>
          <select value={lines[c]?.uom || "box"} onChange={(e) => upd(c, "uom", e.target.value)} className={inp} data-testid={`offer-uom-${c}`}><option value="box">{t("x.uom.box")}</option><option value="piece">{t("x.uom.piece")}</option></select>
          <input type="number" step="0.01" min="0" placeholder="€" value={lines[c]?.offer_price ?? ""} onChange={(e) => upd(c, "offer_price", e.target.value)} className={inp} data-testid={`offer-price-${c}`} />
          <input type="number" min="1" value={lines[c]?.min_qty ?? 1} onChange={(e) => upd(c, "min_qty", e.target.value)} className={inp} data-testid={`offer-min-${c}`} />
        </div>
      ))}
    </div>
  );
};

export const PromotionForm = ({ promo, open, onClose }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState(() => (promo ? { ...EMPTY(), ...promo, lines: Object.fromEntries((promo.items || []).map((i) => [i.item_code, i])) } : EMPTY()));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const f = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const items = form.item_codes.filter((c) => (form.lines || {})[c]?.offer_price > 0).map((c) => ({ item_code: c, uom: form.lines[c].uom || "box", offer_price: Number(form.lines[c].offer_price), min_qty: Number(form.lines[c].min_qty) || 1 }));
      const body = { title_el: form.title_el, title_en: form.title_en, text_el: form.text_el, text_en: form.text_en, item_codes: form.item_codes, items,
        target: form.target, start_date: form.start_date, end_date: form.end_date, active: form.active };
      const { data } = promo ? await api.put(`/promotions/admin/${promo.id}`, body) : await api.post("/promotions/admin", body);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/promotions/admin/${data.id}/image`, fd);
      }
      qc.invalidateQueries({ queryKey: ["adminPromotions"] });
      toast.success(t("admin.saved"));
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? t("admin.invalid") : d === "offer_own_customers_only" ? t("x.offers.ownOnly") : typeof d === "string" ? d : t("common.error"));
    } finally { setBusy(false); }
  };

  const preview = file ? URL.createObjectURL(file) : mediaUrl(promo?.image_url);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto overflow-x-hidden [&_*]:min-w-0" data-testid="promotion-form">
        <DialogHeader><DialogTitle>{t(promo ? "promo.edit" : "promo.new")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="flex h-32 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500" data-testid="promo-image-drop">
            {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <span className="flex items-center gap-2"><ImagePlus className="h-5 w-5" /> {t("promo.upload")}</span>}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="promo-image-input" />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin.titleEl")} value={form.title_el} onChange={f("title_el")} testId="promo-title-el" />
            <Field label={t("admin.titleEn")} value={form.title_en} onChange={f("title_en")} testId="promo-title-en" />
            <Field area label={t("admin.textEl")} value={form.text_el} onChange={f("text_el")} testId="promo-text-el" />
            <Field area label={t("admin.textEn")} value={form.text_en} onChange={f("text_en")} testId="promo-text-en" />
            <label className="text-xs text-slate-500">{t("promo.start")}<DateField value={form.start_date} onChange={(v) => v && f("start_date")(v)} testId="promo-start" /></label>
            <label className="text-xs text-slate-500">{t("promo.end")}<DateField value={form.end_date} onChange={(v) => v && f("end_date")(v)} testId="promo-end" /></label>
          </div>
          <ItemPicker selected={form.item_codes} onToggle={(c) => f("item_codes")(toggle(form.item_codes, c))} />
          <OfferLines codes={form.item_codes} lines={form.lines || {}} set={f("lines")} />
          <TargetPicker target={form.target} set={f("target")} />
          <label className="flex h-11 items-center gap-2 text-sm text-navy"><Switch checked={form.active} onCheckedChange={f("active")} data-testid="promo-active" /> {t("admin.active")}</label>
          <button type="button" onClick={save} disabled={busy} data-testid="promo-save-button" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand text-base font-semibold text-navy disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("admin.save")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
